// AI-CONTEXT: See docs/features/supertenant-common-toc-paths.md
//
// Converts the backend sectorTocPaths model (distinct-org node/link counts,
// keyed by `${column}::${rawKey}`) into the shape Recharts `Sankey` expects:
// nodes with resolved labels + links referencing numeric node indices.

/**
 * @typedef {object} SectorPathNode
 * @property {string} key - `${column}::${rawKey}`
 * @property {string} column - one of the model columns
 * @property {string} rawKey - the taxonomy key (or MSIId for the effect column)
 * @property {number} orgs - distinct organisations at this node
 */

/**
 * @typedef {object} SankeyNode
 * @property {string} name - display label
 * @property {string} column - the model column this node belongs to
 * @property {string} rawKey - the underlying taxonomy key / MSIId
 * @property {number} orgs - distinct-org count
 */

/**
 * @typedef {object} SankeyModel
 * @property {Array<SankeyNode>} nodes
 * @property {Array<{source: number, target: number, value: number}>} links
 * @property {number} companyCount
 * @property {number} pathCount
 * @property {boolean} isEmpty
 */

/**
 * @param {import('../../types/Firebase').SectorPathModel | null} model - backend sectorTocPaths result
 * @param {(column: string, rawKey: string) => string} resolveLabel - maps a node to its display label
 * @returns {SankeyModel}
 */
const buildSectorPathsSankey = (model, resolveLabel) => {
    const empty = { nodes: [], links: [], companyCount: 0, pathCount: 0, isEmpty: true }

    if (!model || !Array.isArray(model.nodes) || !Array.isArray(model.links)) return empty
    // A Sankey needs at least one flow; nodes without links can't render.
    if (model.links.length === 0) return { ...empty, companyCount: model.companyCount || 0, pathCount: model.pathCount || 0 }

    const columnOrder = Array.isArray(model.columns) ? model.columns : []
    const columnRank = (column) => {
        const i = columnOrder.indexOf(column)
        return i === -1 ? columnOrder.length : i
    }

    // Deterministic order: column left-to-right, then most-common node first,
    // then rawKey for stable ties.
    const sortedNodes = [...model.nodes].sort((a, b) => (
        columnRank(a.column) - columnRank(b.column)
        || (b.orgs || 0) - (a.orgs || 0)
        || String(a.rawKey).localeCompare(String(b.rawKey))
    ))

    const keyToIndex = new Map()
    const nodes = sortedNodes.map((n, index) => {
        keyToIndex.set(n.key, index)
        return {
            name: resolveLabel(n.column, n.rawKey),
            column: n.column,
            rawKey: n.rawKey,
            orgs: n.orgs || 0,
        }
    })

    const links = model.links
        .filter((l) => keyToIndex.has(l.source) && keyToIndex.has(l.target) && (l.orgs || 0) > 0)
        .map((l) => ({
            source: keyToIndex.get(l.source),
            target: keyToIndex.get(l.target),
            value: l.orgs,
        }))

    if (links.length === 0) return { ...empty, companyCount: model.companyCount || 0, pathCount: model.pathCount || 0 }

    return {
        nodes,
        links,
        companyCount: model.companyCount || 0,
        pathCount: model.pathCount || 0,
        isEmpty: false,
    }
}

export default buildSectorPathsSankey
